/**
 * Arrow-key grid/list navigation for a container of focusable items.
 *
 * Moved verbatim out of `ui/shell.js` (CB-3) so shell-free modules can import
 * it — shell.js re-exports it, so existing `import { gridKeyNav } from
 * './shell.js'` call sites are untouched. The one addition over the shell
 * version is `escapeUpTo`.
 */

/**
 * @param {HTMLElement} container
 * @param {string} selector - CSS selector for navigable items
 * @param {Object} [opts]
 * @param {number} [opts.columns] - Items per row (auto-detected from layout if omitted)
 * @param {Function} [opts.onSelect] - Called with focused element on Enter/Space
 * @param {boolean} [opts.wrap=true] - Wrap at edges
 * @param {boolean} [opts.roving=true] - Enable roving tabindex (single tab stop)
 * @param {HTMLElement} [opts.escapeUpTo] - ArrowUp from the FIRST row focuses
 *   this element (e.g. the search field above the grid) instead of clamping or
 *   wrapping. ArrowUp from any other row navigates within the grid as usual.
 * @param {(itemEl: HTMLElement) => HTMLElement} [opts.focusTargetOf] - PP-0:
 *   where focus (and the roving tabindex) actually lands for an item. Defaults
 *   to the item itself. A non-interactive item container that carries a real
 *   primary <button> (a collection tile) maps to that button, so the roving
 *   set stays one selector while the focused node is always operable.
 * @param {string} [opts.ignoreWithin] - PP-0: keys pressed on a control matching
 *   this selector INSIDE an item are left to the control (a tile's dismiss
 *   button handles its own Enter); they neither navigate nor select.
 * @returns {Function} cleanup — removes the keydown listener
 */
export function gridKeyNav(
  container,
  selector,
  { columns, onSelect, wrap = true, roving = true, escapeUpTo, focusTargetOf, ignoreWithin } = {}
) {
  const targetOf = (item) => (typeof focusTargetOf === 'function' && focusTargetOf(item)) || item;

  const syncRoving = (preferredEl = null) => {
    const items = [...container.querySelectorAll(selector)];
    if (!items.length || !roving) return;

    let active = preferredEl && items.includes(preferredEl) ? preferredEl : document.activeElement;
    if (!active || !items.includes(active)) {
      // The focused node may be an item's focus target rather than the item.
      const owner = active?.closest?.(selector);
      active = owner && items.includes(owner) ? owner : null;
    }
    if (!active) {
      active = items.find((item) => targetOf(item).getAttribute('tabindex') === '0') || items[0];
    }

    for (const item of items) {
      const target = targetOf(item);
      target.setAttribute('tabindex', item === active ? '0' : '-1');
      // A container that is not its own target must not be a tab stop either.
      if (target !== item) item.removeAttribute('tabindex');
    }
  };

  syncRoving();

  const onFocusIn = (e) => {
    if (!roving) return;
    const focusedItem = e.target.closest(selector);
    if (!focusedItem || !container.contains(focusedItem)) return;
    syncRoving(focusedItem);
  };

  const onPointerDown = (e) => {
    if (!roving) return;
    const clickedItem = e.target.closest(selector);
    if (!clickedItem || !container.contains(clickedItem)) return;
    syncRoving(clickedItem);
  };

  function handler(e) {
    const tag = e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
    if (e.ctrlKey || e.altKey || e.metaKey) return;
    if (ignoreWithin && e.target.closest?.(ignoreWithin) && !e.target.matches?.(selector)) return;

    const items = [...container.querySelectorAll(selector)];
    if (!items.length) return;
    const activeEl = document.activeElement?.closest?.(selector) || document.activeElement;
    const idx = items.indexOf(activeEl);
    if (idx === -1) return;
    // Focus sits on a real <button> that is the item's focus target (a tile's
    // primary): Enter and Space are the button's own activation — the native
    // click that follows is the contract, so neither preventDefault nor
    // onSelect may run, or the activation would be swallowed or doubled.
    const focusedTarget = targetOf(items[idx]);
    const leaveActivationToButton =
      focusedTarget !== items[idx] &&
      focusedTarget === e.target &&
      focusedTarget.tagName === 'BUTTON';

    // (shell.js wrote `let next = idx`, a suppressed no-useless-assignment —
    // every switch arm below either assigns or returns. The suppression could
    // not move with the code, so the assignment went instead.)
    let next;
    const cols = columns || _detectColumns(items);

    switch (e.key) {
      case 'ArrowRight':
        next = idx + 1;
        break;
      case 'ArrowLeft':
        next = idx - 1;
        break;
      case 'ArrowDown':
        next = idx + cols;
        break;
      case 'ArrowUp':
        if (escapeUpTo && idx < cols) {
          e.preventDefault();
          escapeUpTo.focus();
          return;
        }
        next = idx - cols;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        if (leaveActivationToButton) return;
        e.preventDefault();
        if (onSelect) onSelect(items[idx]);
        return;
      default:
        return;
    }

    e.preventDefault();
    if (wrap) next = (next + items.length) % items.length;
    else next = Math.max(0, Math.min(next, items.length - 1));
    const nextEl = items[next];
    if (!nextEl) return;
    if (roving) {
      targetOf(items[idx]).setAttribute('tabindex', '-1');
      targetOf(nextEl).setAttribute('tabindex', '0');
    }
    targetOf(nextEl).focus();
  }

  container.addEventListener('keydown', handler);
  container.addEventListener('focusin', onFocusIn);
  container.addEventListener('pointerdown', onPointerDown);

  /**
   * MK-7. `syncRoving()` above runs ONCE, at setup — and every collection
   * surface calls this while its list is still EMPTY, because `load()` is
   * async. `syncRoving` returns early on an empty set, so the grid ended up
   * with no tab stop at all: not one item carried `tabindex="0"`, and a
   * keyboard-only user could never Tab into the results. A mouse user never
   * saw it, because `pointerdown` re-syncs.
   *
   * The teardown is now a function WITH a `sync` on it, so it stays
   * call-compatible with every existing `cleanups.push(gridKeyNav(...))` while
   * a caller that renders later can re-arm.
   */
  const teardown = () => {
    container.removeEventListener('keydown', handler);
    container.removeEventListener('focusin', onFocusIn);
    container.removeEventListener('pointerdown', onPointerDown);
  };
  teardown.sync = () => syncRoving();
  return teardown;
}

/** Detect grid columns by comparing item offsetTop values. */
function _detectColumns(items) {
  if (items.length < 2) return 1;
  const firstTop = items[0].offsetTop;
  for (let i = 1; i < items.length; i++) {
    if (items[i].offsetTop !== firstTop) return i;
  }
  return items.length;
}
